import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { 
  ScheduledNoticeDTO, 
  ScheduledNoticeResponse, 
  CreateScheduledNoticeRequest, 
  UpdateScheduledNoticeRequest,
  ToggleScheduledNoticeRequest,
  NoticeType,
  ScheduleType,
  NoticeSeverity
} from '../interfaces/scheduled-notice.interfaces';

@Injectable({
  providedIn: 'root'
})
export class ScheduledNoticeService {
  private baseUrl = `${environment.apiUrl}/scheduled-notices`;
  private activeNoticeSubject = new BehaviorSubject<ScheduledNoticeDTO | null>(null);
  public activeNotice$ = this.activeNoticeSubject.asObservable();

  constructor(private http: HttpClient) {}

  // API Calls
  getActiveScheduledNotices(): Observable<ScheduledNoticeResponse> {
    return this.http.get<ScheduledNoticeResponse>(`${this.baseUrl}/active`);
  }

  getAllScheduledNotices(): Observable<ScheduledNoticeResponse> {
    return this.http.get<ScheduledNoticeResponse>(this.baseUrl);
  }

  createScheduledNotice(notice: CreateScheduledNoticeRequest): Observable<ScheduledNoticeDTO> {
    return this.http.post<ScheduledNoticeDTO>(this.baseUrl, notice);
  }

  updateScheduledNotice(id: number, notice: UpdateScheduledNoticeRequest): Observable<ScheduledNoticeDTO> {
    return this.http.put<ScheduledNoticeDTO>(`${this.baseUrl}/${id}`, notice);
  }

  deleteScheduledNotice(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  toggleScheduledNotice(id: number, isActive: boolean): Observable<ScheduledNoticeDTO> {
    return this.http.patch<ScheduledNoticeDTO>(`${this.baseUrl}/${id}/toggle`, { isActive } as ToggleScheduledNoticeRequest);
  }

  // Health check
  healthCheck(): Observable<any> {
    return this.http.get(`${this.baseUrl}/health`);
  }

  // Logica di controllo avvisi
  checkScheduledNotices(): void {
    this.getActiveScheduledNotices().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const now = new Date();
          let activeNotice: ScheduledNoticeDTO | null = null;

          for (const notice of response.data) {
            if (this.shouldShowNotice(notice, now)) {
              const dismissKey = `notice-dismissed-${notice.id}-${this.getCurrentDateString()}`;
              const wasDismissed = localStorage.getItem(dismissKey);
              
              if (!wasDismissed) {
                activeNotice = notice;
                break;
              }
            }
          }

          this.activeNoticeSubject.next(activeNotice);
        }
      },
      error: (error) => {
        console.error('Errore nel recupero degli avvisi schedulati:', error);
      }
    });
  }

  dismissActiveNotice(): void {
    const activeNotice = this.activeNoticeSubject.value;
    if (activeNotice) {
      const dismissKey = `notice-dismissed-${activeNotice.id}-${this.getCurrentDateString()}`;
      localStorage.setItem(dismissKey, 'true');
      this.activeNoticeSubject.next(null);
    }
  }

  // Logica di schedulazione
  private shouldShowNotice(notice: ScheduledNoticeDTO, currentDate: Date): boolean {
    if (!notice.isActive) return false;

    if (notice.type === NoticeType.RECURRING) {
      return this.checkRecurringSchedule(notice, currentDate);
    } else if (notice.type === NoticeType.SINGLE) {
      return this.checkSingleSchedule(notice, currentDate);
    }
    return false;
  }

  private checkRecurringSchedule(notice: ScheduledNoticeDTO, currentDate: Date): boolean {
    if (!notice.scheduleType) return false;

    switch (notice.scheduleType) {
      case ScheduleType.THIRD_WEDNESDAY:
        return this.isThirdWednesday(currentDate) && 
               this.isTimeInRange(currentDate, notice.startTime!, notice.endTime!);
      case ScheduleType.DAILY:
        return this.isTimeInRange(currentDate, notice.startTime!, notice.endTime!);
      case ScheduleType.WEEKLY:
        // Esempio: ogni lunedì (giorno 1)
        return currentDate.getDay() === 1 && 
               this.isTimeInRange(currentDate, notice.startTime!, notice.endTime!);
      case ScheduleType.MONTHLY:
        // Esempio: primo giorno del mese
        return currentDate.getDate() === 1 && 
               this.isTimeInRange(currentDate, notice.startTime!, notice.endTime!);
      default:
        return false;
    }
  }

  private checkSingleSchedule(notice: ScheduledNoticeDTO, currentDate: Date): boolean {
    if (!notice.startDate || !notice.endDate) return false;
    
    const startDate = new Date(notice.startDate);
    const endDate = new Date(notice.endDate);
    
    const isDateInRange = currentDate >= startDate && currentDate <= endDate;
    
    if (isDateInRange && notice.startTime && notice.endTime) {
      return this.isTimeInRange(currentDate, notice.startTime, notice.endTime);
    }
    
    return isDateInRange;
  }

  private isThirdWednesday(date: Date): boolean {
    const year = date.getFullYear();
    const month = date.getMonth();
    const wednesdays = [];
    
    for (let day = 1; day <= 31; day++) {
      const currentDay = new Date(year, month, day);
      if (currentDay.getMonth() !== month) break;
      
      if (currentDay.getDay() === 3) {
        wednesdays.push(day);
      }
    }
    
    return wednesdays.length >= 3 && date.getDate() === wednesdays[2];
  }

  private isTimeInRange(date: Date, startTime: string, endTime: string): boolean {
    // Converte il formato HH:mm:ss in minuti per il confronto
    const currentTime = date.getHours() * 60 + date.getMinutes();
    
    // Parse del formato LocalTime serializzato (HH:mm:ss o HH:mm)
    const parseTime = (timeStr: string): number => {
      const parts = timeStr.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      return hours * 60 + minutes;
    };
    
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    
    return currentTime >= start && currentTime <= end;
  }

  private getCurrentDateString(): string {
    const now = new Date();
    return now.getFullYear() + '-' + 
           String(now.getMonth() + 1).padStart(2, '0') + '-' + 
           String(now.getDate()).padStart(2, '0');
  }
}
